import mixpanel from 'mixpanel-browser'
import Vue from 'vue'

let token = process.env.VUE_APP_MIXPANEL
let needInit = !!token
let activeDurationTracker = null
const snakeCasePattern = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/

let executeSafely = (callback) => {
  try {
    return callback()
  } catch (e) {
    // 分析服务异常不能阻断登录、支付或资源操作等核心业务。
    return undefined
  }
}

if (needInit) {
  try {
    mixpanel.init(token)
  } catch (e) {
    needInit = false
  }
}

let getPageUrl = () => `${window.location.origin}${window.location.pathname}`

let getSourcePage = () => {
  let pathSegments = window.location.pathname.split('/').filter((segment) => {
    if (!segment) {
      return false
    }
    // 动态资源 ID 不参与页面名，避免同一页面产生大量 source_page 值。
    return !/^\d+$/.test(segment) && !/^[0-9a-f-]{20,}$/i.test(segment)
  })
  return (
    pathSegments
      .join('_')
      .replace(/-/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '')
      .toLowerCase() || 'home'
  )
}

let validateEventSchema = (eventName, properties) => {
  if (process.env.NODE_ENV === 'production') {
    return true
  }
  if (!snakeCasePattern.test(eventName)) {
    Vue.util.warn(`Invalid analytics event name: ${eventName}`)
    return false
  }
  let propertyKeys = Object.keys(properties)
  for (let i = 0; i < propertyKeys.length; i += 1) {
    let key = propertyKeys[i]
    if (!snakeCasePattern.test(key)) {
      Vue.util.warn(`Invalid analytics property name: ${key}`)
      return false
    }
  }
  return true
}

let stopDurationTracker = () => {
  if (!activeDurationTracker) {
    return
  }
  document.removeEventListener('visibilitychange', activeDurationTracker.visibilityHandler)
  window.removeEventListener('pagehide', activeDurationTracker.pagehideHandler)
  window.removeEventListener('beforeunload', activeDurationTracker.beforeunloadHandler)
  activeDurationTracker = null
}

let flushDurationTracker = (useBeacon = false) => {
  if (!needInit || !activeDurationTracker || activeDurationTracker.sent) {
    return
  }
  if (activeDurationTracker.visibleStartedAt) {
    activeDurationTracker.visibleDurationMs += Date.now() - activeDurationTracker.visibleStartedAt
    activeDurationTracker.visibleStartedAt = null
  }
  let durationSeconds = Math.round(activeDurationTracker.visibleDurationMs / 1000)
  activeDurationTracker.sent = true
  executeSafely(() => {
    mixpanel.track(
      'checkout_duration_recorded',
      {
        page_url: activeDurationTracker.page_url,
        page_name: activeDurationTracker.page_name,
        product_name: activeDurationTracker.product_name,
        duration_seconds: durationSeconds
      },
      useBeacon ? { transport: 'sendBeacon' } : undefined
    )
  })
  stopDurationTracker()
}

let resetAnalytics = () => {
  if (needInit) {
    // reset 会生成新的匿名身份，先结算当前页面才能保留原用户归属。
    flushDurationTracker(true)
    executeSafely(() => mixpanel.reset())
  }
}

let SDK = {
  install(V) {
    V.prototype.$SDK = {
      set(options) {
        needInit && executeSafely(() => mixpanel.people.set(options))
      },
      identify(id) {
        needInit && executeSafely(() => mixpanel.identify(String(id)))
      },
      setUser(id, options) {
        if (!needInit || id === undefined || id === null || id === -1) {
          return
        }
        // 统一身份设置顺序，避免用户属性依赖 SDK 的 pending identify 队列。
        executeSafely(() => {
          mixpanel.identify(String(id))
          options && mixpanel.people.set(options)
        })
      },
      reset() {
        resetAnalytics()
      },
      track(eventName, properties = {}, options) {
        let eventProperties = Object.assign({}, properties)
        // 个别页面需要保留 URL 查询参数用于分析；未显式传入时仍使用标准化 URL。
        eventProperties.page_url = eventProperties.page_url || getPageUrl()
        if (/_clicked$/.test(eventName)) {
          // 点击事件必须同时说明点击目标和来源页面；调用点可按业务需要覆盖默认值。
          eventProperties.click_name =
            eventProperties.click_name || eventName.replace(/_clicked$/, '')
          eventProperties.source_page = eventProperties.source_page || getSourcePage()
        }
        if (needInit && validateEventSchema(eventName, eventProperties)) {
          executeSafely(() => {
            mixpanel.track(eventName, eventProperties, options)
          })
        }
      },
      startPageDuration({ page_name, product_name }) {
        if (!needInit) {
          return
        }
        // 重复启动时先结算旧页面，避免旧 tracker 被静默覆盖。
        flushDurationTracker()
        activeDurationTracker = {
          page_name,
          product_name,
          page_url: getPageUrl(),
          // 只统计页面真正可见的时长，切到后台后的时间不计入。
          visibleStartedAt: document.visibilityState === 'hidden' ? null : Date.now(),
          visibleDurationMs: 0,
          sent: false
        }
        activeDurationTracker.visibilityHandler = () => {
          if (document.visibilityState === 'hidden') {
            if (activeDurationTracker && activeDurationTracker.visibleStartedAt) {
              activeDurationTracker.visibleDurationMs +=
                Date.now() - activeDurationTracker.visibleStartedAt
              activeDurationTracker.visibleStartedAt = null
            }
          } else if (activeDurationTracker && !activeDurationTracker.visibleStartedAt) {
            activeDurationTracker.visibleStartedAt = Date.now()
          }
        }
        activeDurationTracker.pagehideHandler = () => {
          flushDurationTracker(true)
        }
        activeDurationTracker.beforeunloadHandler = () => {
          flushDurationTracker(true)
        }
        document.addEventListener('visibilitychange', activeDurationTracker.visibilityHandler)
        window.addEventListener('pagehide', activeDurationTracker.pagehideHandler)
        window.addEventListener('beforeunload', activeDurationTracker.beforeunloadHandler)
      },
      endPageDuration() {
        flushDurationTracker()
      }
    }
  }
}

Vue.use(SDK)

export { resetAnalytics }
